import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Lock, Crown, Filter, Sparkles, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Navbar from '../components/Navbar';
import FadeImage from '../components/FadeImage';
import MediaPreviewModal from '../components/MediaPreviewModal';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== ELLA'S EIGEN GALLERY FOTO'S - DEFINITIEF HARDCODED ==============
// NOOIT MEER WIJZIGEN - Correcte UUIDs van Ella's eigen foto's
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_GALLERY_IMAGES = [
  { id: 1, url: `${API_BASE}/api/media/ebd63b84-575a-48d2-a05b-33b154a62162`, title: "Golden Light", tier: "bronze", type: "Digital Art" },       // IMG_5335
  { id: 2, url: `${API_BASE}/api/media/0e12bb36-741e-4307-9d64-22c8b004057f`, title: "Nordic Dreams", tier: "bronze", type: "Digital Art" },      // 56C54729
  { id: 3, url: `${API_BASE}/api/media/18354470-b5a8-42af-b35d-5b25854e4156`, title: "Velvet Shadows", tier: "bronze", type: "Digital Art" },     // IMG_5233
  { id: 4, url: `${API_BASE}/api/media/e85135e0-1c07-4f9f-9f41-113ab6df74b9`, title: "Aurora", tier: "bronze", type: "Digital Art" },             // IMG_5150
  { id: 5, url: `${API_BASE}/api/media/29879b2b-b65d-4245-8093-e6b10be8497b`, title: "Mystic Portrait", tier: "silver", type: "Digital Art" },   // IMG_5113
  { id: 6, url: `${API_BASE}/api/media/0237dce4-162d-4a1a-a8b0-46e0e71f24a9`, title: "Ethereal Beauty", tier: "silver", type: "Digital Art" },   // 750A1496
  { id: 7, url: `${API_BASE}/api/media/b47d028f-1368-451e-92ae-731882b5fc89`, title: "Studio Session", tier: "gold", type: "Digital Art" },      // IMG_5092
  { id: 8, url: `${API_BASE}/api/media/ab593693-bf48-4d47-884e-cbe6b492aea2`, title: "Natural Glow", tier: "gold", type: "Digital Art" },        // IMG_5091
];

export default function Gallery() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // ALWAYS use hardcoded images - no database fetching
  const [galleryItems, setGalleryItems] = useState(DEFAULT_GALLERY_IMAGES);
  const [filter, setFilter] = useState('all');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  // Content filtered by tier - using useMemo to avoid setState in useEffect
  const filteredContent = filter === 'all' ? galleryItems : galleryItems.filter(item => item.tier === filter);

  const openPreview = (index) => {
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const tiers = [
    { value: 'all', label: t('gallery.all') },
    { value: 'bronze', label: t('rewards.bronze.level') + ' Collection' },
    { value: 'silver', label: t('rewards.silver.level') + ' Collection' },
    { value: 'gold', label: t('rewards.gold.level') + ' Collection' },
    { value: 'platinum', label: 'Platinum Collection' },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <SEO 
        title="Gallery"
        description="Ontdek de exclusieve digitale kunstcollectie van Ella Gold. Premium AI-gegenereerde kunstwerken in verschillende stijlen."
      />
      <Navbar />
      
      <div className="pt-24 pb-16 px-6 lg:px-20">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">{t('gallery.badge')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight">
              {t('gallery.title')} <span className="brand-gold text-5xl sm:text-6xl">{t('gallery.titleAccent')}</span>
            </h1>
            <p className="text-gray-400 mt-3 max-w-xl">
              {t('gallery.subtitle')}
            </p>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger 
                className="w-48 bg-white/5 border-white/10 text-white"
                data-testid="gallery-filter"
              >
                <SelectValue placeholder={t('gallery.filter')} />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {tiers.map((tier) => (
                  <SelectItem 
                    key={tier.value} 
                    value={tier.value}
                    className="text-white hover:bg-white/5"
                  >
                    {tier.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mb-8 p-4 glass rounded-xl border border-[#D4AF37]/20 text-center">
          <p className="text-gray-400 text-sm">
            🎨 <span className="text-[#D4AF37]">{t('gallery.premium')}</span> {t('collection.info')}
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredContent.map((item, index) => (
            <div 
              key={item.id}
              className="model-card group relative overflow-hidden rounded-xl border border-white/5 bg-white/5 animate-fade-in cursor-pointer"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`gallery-item-${index}`}
              onClick={() => openPreview(index)}
            >
              <div className="relative aspect-[3/4]">
                <FadeImage 
                  src={item.url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                
                {/* Preview Overlay */}
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <Eye className="w-8 h-8 text-[#D4AF37] mb-3" />
                  <span className="text-sm text-gray-300 mb-4">Bekijk Preview</span>
                  <Button 
                    onClick={(e) => { e.stopPropagation(); navigate('/packages'); }}
                    className="btn-gold px-6"
                    data-testid={`unlock-btn-${index}`}
                  >
                    Koop Toegang
                  </Button>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-mono tracking-wider rounded uppercase">
                    {item.tier}
                  </span>
                  <span className="text-xs text-gray-500 font-mono uppercase">
                    {item.type}
                  </span>
                </div>
                <h3 className="font-medium text-white truncate">{item.title}</h3>
                <p className="text-sm text-gray-400 truncate mt-1">AI-Generated Artwork</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-20 text-center">
          <div className="inline-block p-8 glass rounded-2xl">
            <h3 className="text-2xl font-medium mb-4">
              Want the <span className="gold-text">full collection</span>?
            </h3>
            <p className="text-gray-400 mb-6 max-w-md">
              Get unlimited access to our entire digital art archive with a membership.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/packages')}
                className="btn-gold px-8 py-6 text-lg"
                data-testid="view-packages-cta"
              >
                View Memberships
              </Button>
              <Button 
                onClick={() => navigate('/builder')}
                variant="outline"
                className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-6 text-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Create Custom Art
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Media Preview Modal */}
      <MediaPreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        media={filteredContent.map(item => ({
          ...item,
          preview_url: item.url
        }))}
        initialIndex={previewIndex}
      />
    </div>
  );
}
